import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { handleAuthError } from '../components/AppVersionManager';

const AuthContext = createContext(null);

const API = "/api";

// Safe localStorage access (some mobile browsers block it)
const safeGetItem = (key) => {
  try {
    return localStorage.getItem(key);
  } catch (e) {
    console.warn('localStorage not available:', e);
    return null;
  }
};

const safeSetItem = (key, value) => {
  try {
    localStorage.setItem(key, value);
  } catch (e) {
    console.warn('localStorage not available:', e);
  }
};

const safeRemoveItem = (key) => {
  try {
    localStorage.removeItem(key);
  } catch (e) {
    console.warn('localStorage not available:', e);
  }
};

// Globaler Axios Response Interceptor für Auth-Fehler
// Wird einmal beim Import gesetzt
let interceptorSetup = false;
const setupAuthInterceptor = () => {
  if (interceptorSetup) return;
  interceptorSetup = true;
  
  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      const status = error?.response?.status;
      
      // Bei 401/403: Auth-Daten löschen und zu Login redirecten
      // ABER: Nicht bei Login-Versuchen selbst (die werfen auch 401)
      if ((status === 401 || status === 403) && 
          !error?.config?.url?.includes('/auth/login')) {
        handleAuthError(status);
      }
      
      return Promise.reject(error);
    }
  );
};

// Interceptor beim Import aktivieren
setupAuthInterceptor();

// Debug logging helper
const authDebug = (msg, data = null) => {
  const timestamp = new Date().toISOString().substr(11, 12);
  console.log(`[AUTH ${timestamp}] ${msg}`, data ? JSON.stringify(data) : '');
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [forcePasswordChange, setForcePasswordChange] = useState(false);

  useEffect(() => {
    const token = safeGetItem('token');
    authDebug('Initial auth check', { hasToken: !!token, apiUrl: API });
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchUser = async () => {
    authDebug('fetchUser called - calling /api/me');
    try {
      // WICHTIG: Endpoint ist /api/me (NICHT /api/auth/me)
      const response = await axios.get(`${API}/me`);
      authDebug('fetchUser SUCCESS', { userId: response.data?.id, username: response.data?.username });
      setUser(response.data);
      setForcePasswordChange(response.data?.force_password_change || false);
    } catch (error) {
      authDebug('fetchUser FAILED', { 
        status: error?.response?.status,
        detail: error?.response?.data?.detail,
        message: error?.message 
      });
      console.warn('Auth check failed:', error?.message);
      safeRemoveItem('token');
      delete axios.defaults.headers.common['Authorization'];
    } finally {
      setLoading(false);
    }
  };

  const login = async (username, password) => {
    authDebug('login called', { username, apiUrl: `${API}/auth/login` });
    const response = await axios.post(`${API}/auth/login`, { username, password });
    const { token, user: userData } = response.data;
    
    authDebug('login response received', { hasToken: !!token, hasUser: !!userData, userId: userData?.id });
    
    if (!token || !userData) {
      authDebug('login response INVALID - missing token or user');
      throw new Error('Invalid login response');
    }
    
    safeSetItem('token', token);
    authDebug('token saved to localStorage');
    
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    authDebug('axios default header set');
    
    // Synchron State setzen und Promise zurückgeben das nach State-Update resolvet
    return new Promise((resolve) => {
      setUser(userData);
      setForcePasswordChange(userData?.force_password_change || false);
      
      // Erhöhte Verzögerung für Mobile-Browser um Race Conditions zu vermeiden
      // React 19 braucht mehr Zeit für State-Batching auf langsamen Geräten
      requestAnimationFrame(() => {
        setTimeout(() => {
          authDebug('login complete, resolving promise');
          resolve(userData);
        }, 100);
      });
    });
  };

  const logout = () => {
    safeRemoveItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
    setForcePasswordChange(false);
  };

  const clearForcePasswordChange = () => {
    setForcePasswordChange(false);
    if (user) {
      setUser({ ...user, force_password_change: false });
    }
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      login, 
      logout, 
      loading, 
      forcePasswordChange, 
      clearForcePasswordChange 
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
